import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useAnchoredDropdown } from './selectStyles';

// Reusable single-select combobox with an in-dropdown search box. Matches the
// teal CustomSelect look but adds type-to-filter — used for long option lists
// (specialties, addresses, …). Self-contained: owns its open + search state.
//
// Props:
//   value        current value ('' = the "all"/placeholder option)
//   onChange(v)  called with the chosen value
//   options      [{ value, label }]  (strings are also accepted)
//   placeholder  label shown when nothing is selected (also the "all" row)
//   icon         optional lucide icon component rendered on the left
//   options may carry { disabled, note } to show a row that exists but cannot
//                be chosen, with the reason beside it
//   options may carry `icon`: a node, or ({ active }) => node, drawn before the
//                label in its row and again in the trigger once it is chosen
//                (specialty pickers pass <SpecialtyIcon/>, whose style is a
//                platform-wide setting and never varies with active state)
//   placeholderDisabled / placeholderNote  same, for the placeholder row
//   disabled     greys it out and refuses to open — needed by fields that lock
//                while a save is in flight
//   variant      'default' | 'plain' | 'form'
//                plain drops the trigger's own chrome
//                (border/bg/shadow/width cap) so the control can sit inside an
//                already-styled field wrapper, e.g. the login page's
//                organisation field which must match the email/password boxes
//   searchPlaceholder, className, testId
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  icon: Icon,
  className = '',
  disabled = false,
  variant = 'default',
  placeholderDisabled = false,
  placeholderNote = '',
  testId,
}) {
  const plain = variant === 'plain';
  // 'form': the control is ONE FIELD AMONG MANY in a labelled form (the
  // appointment modal, the tenant form, the master modals) and must be
  // indistinguishable from the native inputs beside it — full width, p-3
  // height, text-sm, border-gray-300. The default stays the compact filter
  // pill; nothing changes anywhere that does not opt in.
  const form = variant === 'form';
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const panelOpen = open && !disabled;
  const { anchorRef, panelRef, panelStyle } = useAnchoredDropdown(panelOpen);

  // Normalise options to { value, label }.
  const opts = useMemo(
    () => options.map((o) => (typeof o === 'object' ? o : { value: o, label: o })),
    [options]
  );

  const selected = opts.find((o) => o.value === value);
  const display = selected ? selected.label : placeholder;
  // The trigger shows the chosen row's icon in the resting (outline) style;
  // the row inside the panel is the one that goes solid when active.
  const selectedIcon = renderOptionIcon(selected?.icon, false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return opts;
    return opts.filter((o) => String(o.label).toLowerCase().includes(term));
  }, [opts, q]);

  // Close on outside click. The panel is portaled to document.body, so it is not
  // inside rootRef — without the panelRef test, clicking an option would count as
  // "outside", close the panel on mousedown and destroy the row before its click
  // landed, making the control impossible to use.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, panelRef]);

  // Focus the search box + reset the query when opening.
  useEffect(() => {
    if (open) { setQ(''); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <div ref={rootRef} className={`relative ${plain || form ? 'w-full' : 'w-full sm:w-56'} ${className}`} data-testid={testId ? `${testId}-container` : undefined}>
      <button
        ref={anchorRef}
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={
          plain
            ? `w-full flex items-center gap-2 pr-7 text-sm sm:text-sm md:text-base bg-transparent focus:outline-none relative ${
                disabled
                  ? 'text-gray-400 cursor-not-allowed'
                  : `${selected ? 'text-gray-800' : 'text-gray-500'} cursor-pointer`
              }`
            : form
              // The native form fields' exact recipe (p-3 rounded-xl
              // border-gray-300 text-sm + the teal focus ring) so this control
              // and a plain <select> in the same row are twins.
              ? `w-full flex items-center gap-2 ${Icon ? 'pl-9' : 'pl-3'} pr-8 py-3 text-sm rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] transition-all duration-300 relative ${
                  disabled
                    ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#4ABDBD] cursor-pointer'
                }`
              : `w-full flex items-center gap-2 pl-9 pr-8 py-2 text-xs sm:text-sm rounded-xl border shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 relative ${
                disabled
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#4ABDBD] cursor-pointer'
              }`
        }
      >
        {Icon && (plain
          ? <Icon className="pointer-events-none w-4 h-4 text-[#4ABDBD] shrink-0" />
          : <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4ABDBD]" />)}
        {selectedIcon && (
          <span className="flex items-center shrink-0 text-[#4ABDBD]">{selectedIcon}</span>
        )}
        <span className="truncate text-left flex-1">{display}</span>
        <ChevronDown className={`absolute ${plain ? 'right-0' : 'right-2.5'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {panelOpen && typeof document !== 'undefined' && createPortal(
        // Portaled + position:fixed (style from the hook): this control lives in
        // filter bars that scroll horizontally, and an absolutely positioned panel
        // was clipped by that overflow-x-auto parent.
        // flex-col so the option list — not the search box — absorbs the height
        // cap when the panel has to squeeze into a short gap.
        <div
          ref={panelRef}
          style={panelStyle}
          data-testid={testId ? `${testId}-dropdown` : undefined}
          className="flex flex-col min-w-[12rem] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
        >
          <div className="p-2 border-b border-gray-100 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                data-testid={testId ? `${testId}-search` : undefined}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30"
              />
            </div>
          </div>
          <div className="max-h-60 min-h-0 overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-[#4ABDBD] scrollbar-track-gray-100">
            {/* "All"/placeholder row */}
            <Option
              label={placeholder}
              active={!value}
              disabled={placeholderDisabled}
              note={placeholderNote}
              onClick={() => pick('')}
            />
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400" data-testid={testId ? `${testId}-empty` : undefined}>No matches</div>
            ) : (
              filtered.map((o, i) => (
                <Option
                  key={o.value ?? i}
                  label={o.label}
                  icon={o.icon}
                  active={o.value === value}
                  disabled={o.disabled}
                  note={o.note}
                  onClick={() => pick(o.value)}
                  testId={testId ? `${testId}-option-${o.value ?? i}` : undefined}
                />
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// PER-OPTION disabled, with an optional reason.
//
// Some lists have rows that exist but cannot be chosen — a subscription plan
// already used, for example. Rendering them as selectable would let somebody
// pick something the server then refuses; hiding them entirely is worse, because
// "where did that plan go?" has no answer on screen. Shown, greyed, and labelled
// with WHY.
// A function-shaped `icon` is a render prop, not a component: it is CALLED
// with { active } rather than mounted, so a caller can flip the drawing
// between outline and solid without declaring a component per row.
const renderOptionIcon = (icon, active) => {
  if (!icon) return null;
  return typeof icon === 'function' ? icon({ active }) : icon;
};

const Option = ({ label, icon, active, onClick, testId, disabled, note }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    data-testid={testId}
    className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm rounded-lg mb-0.5 transition-colors ${
      disabled
        ? 'text-gray-400 cursor-not-allowed'
        : active
          ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
          : 'text-gray-700 hover:bg-[#4ABDBD]/10'
    }`}
  >
    <span className="flex items-center gap-2 min-w-0">
      {icon && <span className="flex items-center shrink-0">{renderOptionIcon(icon, active && !disabled)}</span>}
      <span className="truncate min-w-0">
        {label}
        {note && <span className="ml-1 text-[11px] italic">{note}</span>}
      </span>
    </span>
    {active && !disabled && <Check className="w-4 h-4 flex-shrink-0" />}
  </button>
);
